import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { uploadExerciseVideo } from "@/lib/exerciseMedia";

const VARIANTS = [
  ["main", "Main demo"],
  ["front", "Front view"],
  ["side", "Side view"],
  ["regression", "Regression"],
  ["progression", "Progression"],
  ["common-error", "Common error"],
];

/**
 * Clinician/Admin-only modal that walks a single library exercise through
 * the secure Cloudinary sign → upload → confirm flow. Role gating is done
 * at the router-mount level in ClinicianLibrary (patients never see the
 * "Upload video" button on a card), plus the sign/confirm endpoints
 * themselves are `require_role("clinician", "admin")`.
 */
export default function ExerciseVideoUploader({ exercise, onDone, onClose }) {
  const [variant, setVariant] = useState("main");
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null); // refreshed exercise doc
  const fileRef = useRef(null);

  const pickAndUpload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Choose a video file first"); return; }
    if (!f.type.startsWith("video/")) { toast.error("That's not a video file"); return; }
    if (f.size > 200 * 1024 * 1024) {
      toast.error("Video is larger than 200 MB — trim it first");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const doc = await uploadExerciseVideo({
        file: f, api, exerciseId: exercise.id, variant,
        onProgress: setProgress,
      });
      setResult(doc);
      onDone?.(doc);
      toast.success(`${variant} video uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Upload failed");
    } finally { setUploading(false); }
  };

  const uploadedRow = result?.videos?.find((v) => v.variant === variant);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start md:items-center justify-center p-3"
      style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(3px)" }}
      onClick={onClose}
      data-testid="ex-uploader-modal"
    >
      <div
        role="dialog" aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="fl-card fl-card-elevated w-full max-w-lg my-4 max-h-[92vh] overflow-auto"
        style={{ border: "1px solid rgba(244, 122, 32, 0.28)", boxShadow: "0 2px 12px -6px rgba(0,0,0,0.45)" }}
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b" style={{ borderColor: "var(--fl-border)" }}>
          <div className="min-w-0">
            <div className="fl-label text-[color:var(--fl-orange)]">Upload demo video</div>
            <div className="fl-heading text-lg mt-1 truncate">{exercise.name}</div>
            <div className="text-[11px] mt-1" style={{ color: "var(--fl-muted-2)" }}>
              library_key: <code>{exercise.library_key || "—"}</code>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-zinc-400 hover:text-white" data-testid="ex-uploader-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <label className="block">
            <div className="fl-label mb-1">Variant</div>
            <select
              className="fl-input"
              value={variant}
              onChange={(e) => setVariant(e.target.value)}
              disabled={uploading}
              data-testid="ex-uploader-variant"
            >
              {VARIANTS.map(([v, label]) => (
                <option key={v} value={v}>{label}</option>
              ))}
            </select>
            <div className="text-[11px] mt-1" style={{ color: "var(--fl-muted-2)" }}>
              The server generates the full Cloudinary <code>public_id</code>. You cannot override the folder.
            </div>
          </label>

          <label className="block">
            <div className="fl-label mb-1">Video file (mp4 / mov / webm, max 200 MB)</div>
            <input
              ref={fileRef}
              type="file"
              accept="video/*"
              disabled={uploading}
              className="fl-input"
              data-testid="ex-uploader-file"
            />
          </label>

          {uploading && (
            <div data-testid="ex-uploader-progress">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs" style={{ color: "var(--fl-muted-2)" }}>Uploading… {progress}%</span>
                <Loader2 size={14} className="animate-spin" style={{ color: "var(--fl-orange)" }} />
              </div>
              <div className="h-2 w-full rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                <div
                  className="h-full transition-all"
                  style={{ width: `${progress}%`, background: "var(--fl-orange)" }}
                />
              </div>
            </div>
          )}

          {uploadedRow && (
            <div
              className="rounded-md p-3 flex items-center gap-3"
              style={{
                background: "color-mix(in oklab, #22C55E 10%, transparent)",
                border: "1px solid #22C55E44",
              }}
              data-testid="ex-uploader-success"
            >
              {uploadedRow.thumbnail_url && (
                <img
                  src={uploadedRow.thumbnail_url}
                  alt=""
                  className="w-16 h-16 object-cover rounded"
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-emerald-400 font-semibold text-sm">
                  <CheckCircle2 size={14} /> Verified · {variant}
                </div>
                <div className="text-[11px] mt-0.5 truncate" style={{ color: "var(--fl-muted-2)" }}>
                  {uploadedRow.public_id}
                  {uploadedRow.duration ? ` · ${Math.round(uploadedRow.duration)}s` : ""}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="fl-btn-ghost text-xs"
              disabled={uploading}
              data-testid="ex-uploader-cancel"
            >
              {result ? "Done" : "Cancel"}
            </button>
            <button
              type="button"
              onClick={pickAndUpload}
              className="fl-btn text-xs"
              disabled={uploading}
              data-testid="ex-uploader-submit"
            >
              {uploading ? <><Loader2 size={12} className="animate-spin" /> Uploading…</> : <><Upload size={12} /> Upload {variant}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
